// src/components/onboarding/venue-details-form.tsx
import { useState, type FormEvent } from 'react';
import { motion } from 'motion/react';
import { ArrowRight, Loader2, Store } from 'lucide-react';
import CuisineField from '@/src/components/cuisine-field';


export interface VenueDetailsSubmit {
  name: string;
  cuisine: string;
}

interface VenueDetailsFormProps {
  title?: string;
  subhead?: string;
  submitLabel?: string;
  onSubmit: (data: VenueDetailsSubmit) => Promise<{ error: string | null }>;
  onBack?: () => void;
}

export default function VenueDetailsForm({
  title = 'Set up your venue',
  subhead = 'You can fill in the rest anytime from settings.',
  submitLabel = 'Create venue',
  onSubmit,
  onBack,
}: VenueDetailsFormProps) {
  const [name, setName] = useState('');
  const [cuisine, setCuisine] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    const { error: err } = await onSubmit({ name: name.trim(), cuisine: cuisine.trim() });
    setBusy(false);
    if (err) setError(err);
  }

  return (
    <div className="fixed inset-0 bg-black text-white flex flex-col items-center justify-center px-6">
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-full max-w-sm">
        <div className="flex flex-col items-center gap-3 mb-8">
          <Store size={36} className="text-leaf-300" />
          <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
          <p className="text-[11px] text-neutral-500 text-center">{subhead}</p>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider">Venue name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder="e.g. Maydanoz" className="w-full h-12 bg-neutral-900 border border-white/10 rounded-xl px-4 text-sm text-white placeholder:text-neutral-600 focus:border-white/40 focus:outline-none" />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider">Cuisine (optional)</label>
            <CuisineField value={cuisine} onChange={setCuisine} selectClassName="w-full h-12 bg-neutral-900 border border-white/10 rounded-xl px-4 text-sm text-white focus:border-white/40 focus:outline-none" />
          </div>
          {error && <p className="text-[11px] text-red-400 px-1">{error}</p>}
          <motion.button type="submit" disabled={busy || !name.trim()} whileTap={{ scale: 0.97 }} className="w-full h-12 mt-2 bg-white text-black rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-40">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <>{submitLabel} <ArrowRight size={16} /></>}
          </motion.button>
        </form>
        {onBack && (
          <button onClick={onBack} className="w-full text-center text-[11px] text-neutral-600 hover:text-neutral-400 mt-6">Back</button>
        )}
      </motion.div>
    </div>
  );
}